// ─── Pièces jointes d'un message : photo, fichier, position ───────
// Les modules Expo sont chargés en `require` PROTÉGÉ — patron déjà retenu pour
// `expo-location` dans `geo/itineraire.js` : présent → la fonction marche ;
// absent (app qui ne l'embarque pas, vieux build) → repli propre, JAMAIS de
// crash. Le Core est monté par cinq apps : il ne peut pas exiger d'elles une
// dépendance dont elles n'ont pas l'usage.
//
// Le stockage passe par `services/stockage.js` (générique) sous
// `messagerie/<discussionId>/…` — chemin déclaré dans `storage.rules`.
import { Platform } from 'react-native';
import { uploadFichier } from '../services/stockage';

// Un `require(variable)` est REFUSÉ par Metro (« Invalid call ») : il résout les
// modules statiquement, à la construction du bundle. Chaque module a donc son
// propre chargeur, avec une chaîne LITTÉRALE — c'est aussi ce que fait
// `geo/itineraire.js`, et la raison n'y était pas écrite.
const chargerImagePicker = () => { try { return require('expo-image-picker'); } catch { return null; } };
const chargerDocumentPicker = () => { try { return require('expo-document-picker'); } catch { return null; } };
const chargerLocation = () => { try { return require('expo-location'); } catch { return null; } };

export const imagePickerDispo = () => !!chargerImagePicker();
export const documentPickerDispo = () => !!chargerDocumentPicker();
export const positionDispo = () =>
  !!chargerLocation() || (typeof navigator !== 'undefined' && !!navigator.geolocation);

// Renvoie { uri, nom } ou null si annulé / module absent. On ne lève JAMAIS :
// un choix de fichier annulé n'est pas une erreur, c'est le cas courant.
export async function choisirImage({ camera = false } = {}) {
  const IP = chargerImagePicker();
  if (!IP) return null;
  const perm = camera
    ? await IP.requestCameraPermissionsAsync()
    : await IP.requestMediaLibraryPermissionsAsync();
  if (!perm?.granted) return null;
  const res = camera
    ? await IP.launchCameraAsync({ quality: 0.7 })
    : await IP.launchImageLibraryAsync({ quality: 0.7, mediaTypes: IP.MediaTypeOptions?.Images });
  const a = res?.assets?.[0];
  return res?.canceled || !a ? null : { uri: a.uri, nom: a.fileName || '' };
}

export async function choisirDocument() {
  const DP = chargerDocumentPicker();
  if (!DP) return null;
  const res = await DP.getDocumentAsync({ copyToCacheDirectory: true });
  const a = res?.assets?.[0];
  return res?.canceled || !a ? null : { uri: a.uri, nom: a.name || '' };
}

// Position courante : module natif d'abord, repli navigateur sur le web.
export async function positionCourante() {
  const L = chargerLocation();
  if (L) {
    const perm = await L.requestForegroundPermissionsAsync();
    if (!perm?.granted) return null;
    const p = await L.getCurrentPositionAsync({});
    return { lat: p.coords.latitude, lng: p.coords.longitude };
  }
  if (typeof navigator === 'undefined' || !navigator.geolocation) return null;
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { timeout: 8000 },
    );
  });
}

// Téléverse et renvoie le `media` à ranger dans le message.
// `chemin` est CONSERVÉ à côté de l'URL : c'est lui qui permettra de supprimer
// réellement le fichier quand l'auteur supprimera son message — une URL de
// téléchargement ne suffit pas à retrouver l'objet.
export async function televerser(uri, discussionId, { nom } = {}) {
  const { url, chemin } = await uploadFichier(uri, `messagerie/${discussionId}`);
  return nom ? { url, chemin, nom } : { url, chemin };
}

// Aperçu OSM statique, sans clé — marche en OTA (même choix que `geo/`).
export const apercuCarte = (lat, lng, { z = 15, w = 320, h = 160 } = {}) =>
  `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=${z}&size=${w}x${h}&markers=${lat},${lng},red-pushpin`;

export const lienCarte = (lat, lng) =>
  Platform.OS === 'ios'
    ? `http://maps.apple.com/?ll=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
